import React, { PureComponent } from 'react';
import _ from 'lodash';
import moment from 'moment';
import {
  Button,
  Card,
  Divider,
  Icon,
  Input,
  Grid,
  List,
  Loader,
  Message,
  Popup,
  Table,
} from 'semantic-ui-react';

import { compose } from 'recompose';
import Pagination from 'dyzio-shared-components/lib/components/pagination';

import hasUserInfo from '../../hocs/has-user-info';
import hasRESTClient from '../../hocs/has-rest-client';

import TargetLink from '../../components/target-link';

import { renderCurrencySymbol, renderCurrencyQuantity } from '../../utils/helpers/currencies';


export const INVENTORY_HELP_LINK = 'https://help.dyzio.co/en/articles/4539130-setting-up-your-inventory';


const PER_PAGE = 30;

const helpLinkStyle = {
  color: 'rgb(32, 196, 158)',
};

const headerCellStyle = {
  textTransform: 'uppercase',
  fontSize: 13,
  color: '#808080',
};

const labelStyle = {
  fontSize: 11,
  textTransform: 'uppercase',
};

const dividerStyle = {
  marginTop: 10,
};

const addButtonStyle = {
  marginTop: 20,
  height: 41,
};

const containerCardStyle = {
  minHeight: 700,
  display: 'block',
};

const rightBorderStyle = {
  borderRight: '2px solid #808080',
};

class OrganisationInventory extends PureComponent {
  //
  constructor() {
    super();
    this.state = {
      searchTerm: '',
      updatedMonetaryValue: '',
      updatedItemValue: '',
      newItemValue: '',
      newMonetaryValue: 0,
      isAddingANewItem: false,
      items: [],
      isLoadingItems: true,
      isError: false,
      itemUpdated: false,
      isSearching: false,
      totalPages: null,
      currentPage: 0,
    };
  }

  componentDidMount() {
    this.loadInventoryItems();
  }

  loadInventoryItems = async (searchTerm = '') => {
    const { restClient } = this.props;
    const organisationId = _.get(this.props, 'organisation.id');

    const params = {
      'filter[organisation]': organisationId,
    };

    if (!_.isEmpty(_.trim(searchTerm))) {
      params['filter[value]'] = `:${searchTerm}`;
      this.setState({
        isSearching: true,
        searchTerm,
      });
    }

    params['page[offset]'] = this.state.currentOffset;
    params['page[limit]'] = PER_PAGE;
    params.sort = '-createdDate';

    const response = await restClient.get('inventoryItems', { params })
      .catch(() => this.setState({
        isError: true,
        isSearching: false,
        isLoadingItems: false,
        totalPages: 1,
        items: [],
        currentOffset: 1,
      }));
    if (response) {
      const data = _.get(response, 'data.data', []);
      const page = _.get(response, 'data.meta.page');
      this.setState({
        isLoadingItems: false,
        items: data,
        isSearching: false,
        totalPages: _.ceil(_.divide(_.get(page, 'total', 0) / PER_PAGE)),
        currentOffset: _.get(page, 'offset', 0),
      });
    }
  }

  onAddNewInventoryItem = async () => {
    const { restClient } = this.props;
    const { newItemValue, newMonetaryValue } = this.state;
    const organisationId = _.get(this.props, 'organisation.id');
    const userName = _.get(this.props, 'userInfo.name');
    const data = {
      attributes: {
        value: newItemValue,
        monetaryValue: newMonetaryValue,
        organisation: {
          type: 'organisations',
          id: organisationId,
        },
        createdBy: userName,
      },
    };
    this.setState({
      isAddingANewItem: true,
    }, async () => {
      await restClient.post('inventoryItems', {
        data,
      });
      this.setState({
        isAddingANewItem: false,
        newItemValue: '',
        newMonetaryValue: 0,
      }, this.loadInventoryItems);
    });
  }

  onInputChanged = (event, { name, value }) => {
    this.setState({
      [name]: value,
    });
  }

  onSearchValueChanged = _.debounce((event, { value }) => {
    this.loadInventoryItems(value);
  }, 500)

  onUpdateItem = async inventoryItemId => {
    const { restClient } = this.props;
    const { updatedItemValue, updatedMonetaryValue } = this.state;
    const attributes = {};
    if (updatedItemValue) {
      _.set(attributes, 'value', updatedItemValue);
    }
    if (updatedMonetaryValue) {
      _.set(attributes, 'monetaryValue', updatedMonetaryValue);
    }
    if (_.isEmpty(attributes)) return;
    await restClient.patch(`inventoryItems/${inventoryItemId}`, {
      data: {
        attributes,
      },
    });
    this.setState({
      itemUpdated: true,
      updatedItemValue: '',
      updatedMonetaryValue: 0,
    }, this.loadInventoryItems);
  }

  onItemDelete = async id => {
    if (_.isNil(id)) return;
    const { restClient } = this.props;
    await restClient.delete(`inventoryItems/${id}`);
    this.loadInventoryItems();
  }

  onPopupClose = () => {
    this.setState({
      itemUpdated: false,
    });
  }

  onPageDecrement = () => {
    const { currentOffset, searchTerm } = this.state;
    if (currentOffset <= 0) return;
    this.setState({
      currentOffset: _.subtract(currentOffset, PER_PAGE),
    }, () => this.loadInventoryItems(searchTerm));
  }

  onPageIncrement = () => {
    const { currentOffset, totalPages, searchTerm } = this.state;
    if (currentOffset > totalPages) return;
    this.setState({
      currentOffset: _.add(currentOffset, PER_PAGE),
    }, () => this.loadInventoryItems(searchTerm));
  }

  render() {
    const {
      newItemValue,
      newMonetaryValue,
      isAddingANewItem,
      items,
      isLoadingItems,
      itemUpdated,
      isSearching,
      searchTerm,
      totalPages,
      currentOffset,
    } = this.state;

    const currency = _.get(this.props, 'organisation.defaultCurrency', 'GBP');

    const addButtonProps = {
      disabled: _.isEmpty(_.trim(newItemValue)) || _.toNumber(newMonetaryValue) < 1,
      loading: isAddingANewItem,
      onClick: this.onAddNewInventoryItem,
      icon: true,
      color: 'pink',
      fluid: true,
      style: addButtonStyle,
      labelPosition: 'left',
    };

    const monetaryLabelProps = { basic: true, content: renderCurrencySymbol(currency), style: { paddingTop: 13 } };

    const currentPage = _.floor(currentOffset / PER_PAGE) + 1;

    return (
      <div>
        <Card fluid>
          <Card.Content>
            <p>
              Add items that your organisations gifts to influencers here.<br />
              The system will allow you to assign the items to the influencer
              in the campaign<br /> which will allow you to track the cost of the collaboration to your business.<br />
              The system will pull the data through into the performance reports such as the ROI report.
            </p>
          </Card.Content>
          <Card.Content extra>
            <TargetLink style={helpLinkStyle} renderIf href={INVENTORY_HELP_LINK}>
              <Icon name="help" circular />
              click here for help
            </TargetLink>
          </Card.Content>
        </Card>
        <Card fluid>
          <Card.Content>
            <Grid fluid>
              <Grid.Row>
                <Grid.Column width={5} style={rightBorderStyle}>
                  <label style={headerCellStyle} htmlFor="newItemValue">search</label>
                  <Input
                    onChange={this.onSearchValueChanged}
                    fluid
                    icon={<Icon name={isSearching ? 'refresh' : 'search'} loading={isSearching} />}
                    placeholder="Search..."
                  />
                </Grid.Column>
                <Grid.Column width={5}>
                  <label style={headerCellStyle} htmlFor="newItemValue">title</label>
                  <Input
                    name="newItemValue"
                    fluid
                    value={newItemValue}
                    onChange={this.onInputChanged}
                    placeholder="Add a new iventory item"
                  />
                </Grid.Column>
                <Grid.Column width={3}>
                  <label style={headerCellStyle} htmlFor="monetaryValue">monetary value</label>
                  <Input
                    id="newMonetaryValue"
                    name="newMonetaryValue"
                    fluid
                    step="1"
                    label={monetaryLabelProps}
                    labelPosition="right"
                    type="number"
                    value={newMonetaryValue}
                    onChange={this.onInputChanged}
                    placeholder="Monetary value"
                  />
                </Grid.Column>
                <Grid.Column width={3}>
                  <Button
                    {...addButtonProps}
                  >
                    <Icon name="plus" />
                   Add item
                  </Button>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Card.Content>
          <Card.Content style={containerCardStyle}>
            {
              isLoadingItems && <Loader active content="Loading items" />
            }
            {
              !isLoadingItems && _.isEmpty(items) && _.isEmpty(_.trim(searchTerm)) && <Message>No inventory items created yet</Message>
            }
            {
              !isLoadingItems && _.isEmpty(items) && !_.isEmpty(_.trim(searchTerm)) && <Message>No inventory items found</Message>
            }
            {
              !isLoadingItems && !_.isEmpty(items) &&
              <Table basic="very" textAlign="center">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={headerCellStyle} textAlign="left">
                    title
                    </Table.HeaderCell>
                    <Table.HeaderCell style={headerCellStyle}>
                    created date
                    </Table.HeaderCell>
                    <Table.HeaderCell style={headerCellStyle}>
                    monetary value
                    </Table.HeaderCell>
                    <Table.HeaderCell style={headerCellStyle}>
                    actions
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {
                    _.map(items, item => (
                      <Table.Row key={_.get(item, 'id')}>
                        <Table.Cell width={4} textAlign="left">
                          { _.get(item, 'attributes.value') }
                        </Table.Cell>
                        <Table.Cell width={4}>
                          { moment(_.get(item, 'attributes.createdDate')).format('DD/MM/YY') }
                        </Table.Cell>
                        <Table.Cell width={2}>
                          { renderCurrencyQuantity({
                            currency,
                            amount: _.get(item, 'attributes.monetaryValue', 0),
                          }) }
                        </Table.Cell>
                        <Table.Cell width={4}>
                          <List horizontal>
                            <List.Item>
                              <Popup
                                onClose={this.onPopupClose}
                                content={
                                  <div>
                                    {
                                      itemUpdated && <Message positive fluid>Item updated</Message>
                                    }
                                    <label style={labelStyle} htmlFor="updatedItemValue">Title</label>
                                    <Input
                                      id="updatedItemValue"
                                      onChange={this.onInputChanged}
                                      name="updatedItemValue"
                                      defaultValue={_.get(item, 'attributes.value')}
                                    />
                                    <div style={dividerStyle} />
                                    <label style={labelStyle} htmlFor="updatedMonetaryValue">Monetary Value</label>
                                    <Input
                                      id="updatedMonetaryValue"
                                      onChange={this.onInputChanged}
                                      name="updatedMonetaryValue"
                                      defaultValue={_.get(item, 'attributes.monetaryValue')}
                                    />
                                    <div style={dividerStyle} />
                                    <Button
                                      color="pink"
                                      inverted
                                      fluid
                                      content="Update item"
                                      onClick={() => this.onUpdateItem(_.get(item, 'id'))}
                                    />
                                  </div>
                                }
                                on="click"
                                trigger={<Button icon="pencil" circular inverted color="pink" />}
                              />
                            </List.Item>
                            <List.Item>
                              <Popup
                                content={
                                  <div>
                                  Are you sure?
                                    <Divider hidden fitted />
                                    <Button
                                      inverted
                                      color="pink"
                                      fluid
                                      content="Yes"
                                      onClick={() => this.onItemDelete(_.get(item, 'id'))}
                                    />
                                  </div>
                                }
                                on="click"
                                pinned
                                trigger={<Button icon="trash" circular inverted color="pink" />}
                              />
                            </List.Item>
                          </List>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  }
                </Table.Body>
              </Table>
            }
          </Card.Content>
          {
            !_.isEmpty(items) &&
            <Card.Content extra>
              <Pagination
                onPageIncrement={this.onPageIncrement}
                onPageDecrement={this.onPageDecrement}
                totalPages={totalPages}
                currentPage={currentPage}
              />
            </Card.Content>
          }
        </Card>
      </div>
    );
  }
}

export default compose(
  hasRESTClient,
  hasUserInfo,
)(OrganisationInventory);



/** WEBPACK FOOTER **
 ** ./src/containers/organisation-inventory/index.js
 **/